import type {MyDocument} from '@appManagers/appDocsManager';
import MEDIA_MIME_TYPES_SUPPORTED from '@environment/mediaMimeTypesSupport';
import cancelEvent from '@helpers/dom/cancelEvent';
import {attachClickEvent} from '@helpers/dom/clickEvent';
import findUpClassName from '@helpers/dom/findUpClassName';
import findUpTag from '@helpers/dom/findUpTag';
import setInnerHTML from '@helpers/dom/setInnerHTML';
import mediaSizes from '@helpers/mediaSizes';
import SearchListLoader from '@helpers/searchListLoader';
import {Message, MessageMedia, WebPage} from '@layer';
import confirmationPopup from '@components/confirmationPopup';
import appDownloadManager from '@lib/appDownloadManager';
import appImManager from '@lib/appImManager';
import {MyMessage} from '@appManagers/appMessagesManager';
import {MyPhoto} from '@appManagers/appPhotosManager';
import canSaveMessageMedia from '@appManagers/utils/messages/canSaveMessageMedia';
import getMediaFromMessage from '@appManagers/utils/messages/getMediaFromMessage';
import wrapRichText from '@richTextProcessor/wrapRichText';
import {MediaSearchContext} from '@components/appMediaPlaybackController';
import AppMediaViewerBase, {MEDIA_VIEWER_CLASSNAME} from '@components/mediaViewer/base';
import overlayAvatarVideoOnMover from '@components/mediaViewer/avatarVideo';
import {ButtonMenuItemOptionsVerifiable} from '@components/buttonMenu';
import showDeleteMessagesPopup from '@components/popups/deleteMessages';
import showForwardPopup from '@components/popups/forward';
import Scrollable from '@components/scrollable';
import appSidebarRight from '@components/sidebarRight';
import AppSharedMediaTab from '@components/sidebarRight/tabs/sharedMediaTab';
import {ChatType} from '@components/chat/chatType';
import getFwdFromName from '@appManagers/utils/messages/getFwdFromName';
import TranslatableMessage from '@components/translatableMessage';
import {MAX_FILE_SAVE_SIZE} from '@appManagers/constants';
import I18n, {i18n, LangPackKey} from '@lib/langPack';
import wrapEmojiText from '@richTextProcessor/wrapEmojiText';
import wrapWebPageDescription from '@components/wrappers/webPageDescription';
import Button from '@components/button';
import onQuoteClick from '@helpers/dom/onQuoteClick';
import {canCopyMediaToClipboard} from '@helpers/copyMediaToClipboard';
import {setButtonLoader} from '@components/putPreloader';
import copyMessageMediaWithFeedback from '@components/copyMessageMediaWithFeedback';
import {canEditMessageMediaWithEditor, getEditMediaLangKey} from '@components/chat/editMessageMedia';

type AppMediaViewerTargetType = {
  element: HTMLElement,
  mid: number,
  peerId: PeerId,
  message?: MyMessage,
  index?: number
};

export const onMediaCaptionClick = (caption: HTMLElement, e: MouseEvent) => {
  const a = findUpTag(e.target, 'A');
  const spoiler = findUpClassName(e.target, 'spoiler');
  const quoteDiv = findUpClassName(e.target, 'quote-like-collapsable');
  const isSpoilerVisible = caption.classList.contains('is-spoiler-visible');
  if(quoteDiv && !a && (!spoiler || isSpoilerVisible)) {
    if(onQuoteClick(e, quoteDiv)) {
      return;
    }
  }

  if(!a || a.classList.contains('timestamp')) {
    return;
  }

  if(a instanceof HTMLAnchorElement && (!spoiler || isSpoilerVisible)) { // close viewer if it's t.me/ redirect
    const onclick = a.getAttribute('onclick');
    if(!onclick || onclick.includes('showMaskedAlert')) {
      return;
    }

    cancelEvent(e);
    return () => {
      a.click();
    };
  }
};

export default class AppMediaViewer extends AppMediaViewerBase<'caption', 'delete' | 'forward' | 'copy' | 'edit', AppMediaViewerTargetType> {
  protected listLoader: SearchListLoader<AppMediaViewerTargetType>;
  protected btnMenuForward: ButtonMenuItemOptionsVerifiable;
  protected btnMenuCopy: ButtonMenuItemOptionsVerifiable;
  protected btnMenuDownload: ButtonMenuItemOptionsVerifiable;
  protected btnMenuDelete: ButtonMenuItemOptionsVerifiable;
  protected btnMenuEdit: ButtonMenuItemOptionsVerifiable;
  private deleteAsChatPhoto = false;
  private btnMenuEditText = new I18n.IntlElement({key: 'EditThisPhoto'});
  private videoAvatarCleanup?: () => void;

  get searchContext() {
    return this.listLoader.searchContext;
  }

  constructor(protected local?: boolean, sponsored?: boolean) {
    super(new SearchListLoader({
      processItem: (item) => {
        const isForDocument = this.searchContext.inputFilter._ === 'inputMessagesFilterDocument';
        const {mid, peerId} = item;
        const media = getMediaFromMessage(item, true);

        if(!media) return;

        if(isForDocument && !AppMediaViewer.isMediaCompatibleForDocumentViewer(media)) {
          return;
        }

        return {element: null as HTMLElement, mid, peerId};
      }
    }), ['edit', 'delete', 'forward', 'copy'], sponsored ? 60 : 0);

    this.buttons.copy.classList.add('media-viewer-copy-button');
    this.buttons.copy.setAttribute('aria-label', i18n('MediaViewer.Context.Copy').textContent);

    this.listLoader.onEmptied = () => {
      this.close();
    };

    /* const stub = document.createElement('div');
    stub.classList.add(MEDIA_VIEWER_CLASSNAME + '-stub');
    this.content.main.prepend(stub); */

    this.content.caption = document.createElement('div');
    this.content.caption.classList.add(MEDIA_VIEWER_CLASSNAME + '-caption', 'spoilers-container'/* , 'media-viewer-stub' */);

    let captionTimeout: number;
    const setCaptionTimeout = () => {
      if(captionTimeout) {
        clearTimeout(captionTimeout);
      }

      captionTimeout = window.setTimeout(() => {
        captionTimeout = undefined;
        this.content.caption.classList.remove('is-focused');
      }, 800);
    };
    this.content.caption.addEventListener('touchstart', () => {
      if(!mediaSizes.isMobile) return;

      this.content.caption.classList.add('is-focused');

      if(captionTimeout) {
        clearTimeout(captionTimeout);
        captionTimeout = undefined;
      }

      document.addEventListener('touchend', setCaptionTimeout, {once: true});
    });

    const captionScrollable = new Scrollable(this.content.caption);
    captionScrollable.onAdditionalScroll = setCaptionTimeout;

    // this.content.main.append(this.content.caption);
    this.wholeDiv.append(this.content.caption);

    attachClickEvent(this.buttons.delete, this.onDeleteClick);
    attachClickEvent(this.buttons.copy, this.onCopyMediaClick);
    attachClickEvent(this.buttons.edit, this.onEditClick);

    const buttons: ButtonMenuItemOptionsVerifiable[] = [this.btnMenuEdit = {
      icon: 'brush',
      textElement: this.btnMenuEditText.element,
      onClick: this.onEditClick,
      verify: () => false
    }, this.btnMenuForward = {
      icon: 'forward',
      text: 'Forward',
      onClick: this.onForwardClick
    }, this.btnMenuCopy = {
      icon: 'copy',
      text: 'MediaViewer.Context.Copy',
      onClick: this.onCopyMediaClick,
      keepOpen: true,
      verify: () => false
    }, this.btnMenuDownload = {
      icon: 'download',
      text: 'MediaViewer.Context.Download',
      onClick: this.onDownloadClick
    }, this.btnMenuDelete = {
      icon: 'delete',
      className: 'danger',
      text: 'Delete',
      onClick: this.onDeleteClick
    }];

    this.setBtnMenuToggle(buttons);

    // * constructing html end

    this.setListeners();
  }

  protected setListeners() {
    super.setListeners();
    attachClickEvent(this.buttons.forward, this.onForwardClick);
    attachClickEvent(this.author.container, this.onAuthorClick);

    const onClick = (e: MouseEvent) => {
      const callback = onMediaCaptionClick(this.content.caption, e);
      if(callback) {
        this.close().then(() => {
          this.content.caption.removeEventListener('click', onClick, {capture: true});
          callback();
        });
        return false;
      }
    };

    this.content.caption.addEventListener('click', onClick, {capture: true});
  }

  /* public close(e?: MouseEvent) {
    const good = !this.setMoverAnimationPromise;
    const promise = super.close(e);

    if(good) { // clear
      this.currentMessageId = 0;
      this.peerId = 0;
    }

    return promise;
  } */

  protected getMessageByPeer(peerId: PeerId, mid: number) {
    return this.searchContext.isScheduled ? this.managers.appMessagesManager.getScheduledMessageByPeer(peerId, mid) : this.managers.appMessagesManager.getMessageByPeer(peerId, mid);
  }

  onPrevClick = async(target: AppMediaViewerTargetType) => {
    this.openMedia({
      message: this.local ? target.message : await this.getMessageByPeer(target.peerId, target.mid),
      index: target.index,
      target: target.element,
      fromRight: -1
    });
  };

  onNextClick = async(target: AppMediaViewerTargetType) => {
    this.openMedia({
      message: this.local ? target.message : await this.getMessageByPeer(target.peerId, target.mid),
      index: target.index,
      target: target.element,
      fromRight: 1
    });
  };

  onDeleteClick = async() => {
    const target = this.target;
    if(this.deleteAsChatPhoto) {
      try {
        await confirmationPopup({
          titleLangKey: 'Delete',
          descriptionLangKey: 'AreYouSureDeletePhoto',
          button: {
            langKey: 'Delete',
            isDanger: true
          }
        });
      } catch{
        return;
      }

      await this.managers.appChatsManager.editPhoto(target.peerId.toChatId());
      this.target = {element: this.content.media} as any;
      this.close();
      return;
    }

    showDeleteMessagesPopup(
      target.peerId,
      [target.mid],
      ChatType.Chat,
      () => {
        this.target = {element: this.content.media} as any;
        this.close();
      }
    );
  };

  onEditClick = async() => {
    const {peerId, mid} = this.target;
    const chat = appImManager.chat;
    if(chat?.peerId !== peerId) {
      return;
    }

    // the editor is a fullscreen overlay of its own — get out of the way first,
    // which also puts the bubble back on screen for its open animation
    try {
      await this.close();
    } catch{
      return; // another close is already animating
    }

    chat.input.initMessageMediaEditing(mid);
  };

  onForwardClick = () => {
    const target = this.target;
    if(target.mid) {
      // appSidebarRight.forwardTab.open([target.mid]);
      showForwardPopup({
        [target.peerId]: [target.mid]
      }, () => {
        return this.close();
      });
    }
  };

  onAuthorClick = async(e: MouseEvent) => {
    let {mid, peerId, message} = this.target;
    if(mid && mid !== Number.MAX_SAFE_INTEGER) {
      const threadId = this.searchContext.threadId;
      message ||= await this.getMessageByPeer(peerId, mid);
      this.close(e)
      // .then(() => mediaSizes.isMobile ? appSidebarRight.sharedMediaTab.closeBtn.click() : Promise.resolve())
      .then(async() => {
        if(mediaSizes.isMobile) {
          const tab = appSidebarRight.getTab(AppSharedMediaTab);
          if(tab) {
            tab.close();
          }
        }

        appImManager.setInnerPeer({
          peerId: message.peerId,
          lastMsgId: mid,
          threadId
        });
      });
    }
  };

  onDownloadClick = async(_: any, docId?: DocId) => {
    if(docId) {
      const doc = await this.managers.appDocsManager.getDoc(docId);
      appDownloadManager.downloadToDisc({media: doc, queueId: appImManager.chat.bubbles.lazyLoadQueue.queueId});
      return;
    }
    const {message, index} = this.target;
    const media = getMediaFromMessage(message, true, index);
    if(!media) return;
    appDownloadManager.downloadToDisc({media, queueId: appImManager.chat.bubbles.lazyLoadQueue.queueId});
  };

  onCopyMediaClick = () => {
    const {message, index} = this.target;
    copyMessageMediaWithFeedback({
      message,
      index,
      button: this.btnMenuCopy,
      cleanup: setButtonLoader(this.buttons.copy as HTMLButtonElement)
    });
  };

  private setCaption(message: MyMessage) {
    const isSponsored = !!(message as Message.message).pFlags.sponsored;
    if(isSponsored) {
      this.author.nameEl.append(i18n('SponsoredMessageAd'));
    }

    const media = getMediaFromMessage(message, true);
    const loadPromises: Promise<any>[] = [];
    const richTextOptions: Parameters<typeof wrapRichText>[1] = {
      maxMediaTimestamp: ((media as MyDocument)?.type === 'video' && (media as MyDocument).duration) || undefined,
      textColor: 'white',
      loadPromises
    };

    let hasCaption: boolean;
    let html: HTMLElement;
    if(isSponsored) {
      const sponsoredMessage = (message as Message.message).sponsoredMessage;
      const webPage = ((message as Message.message).media as MessageMedia.messageMediaWebPage).webpage as WebPage.webPage;
      html = document.createElement('div');
      hasCaption = true;
      const b = document.createElement('b');
      b.append(wrapEmojiText(sponsoredMessage.title));
      html.append(
        b,
        '\n',
        wrapWebPageDescription(webPage, {...richTextOptions, entities: webPage.entities}, true)
      );

      const button = Button('btn-primary media-viewer-caption-button', {noRipple: true});
      button.append(wrapEmojiText(sponsoredMessage.button_text));
      this.content.caption.append(button);
      this.content.caption.classList.add('has-button');

      attachClickEvent(button, () => {
        this.close().then(() => {
          appImManager.onSponsoredBoxClick(message as Message.message);
        });
      });
    } else if(hasCaption = !!(message as Message.message).message) {
      html = TranslatableMessage({
        peerId: message.peerId,
        message: message as Message.message,
        middleware: this.content.mover.middlewareHelper.get(),
        richTextOptions
      });
      this.saveTimestamps(html, loadPromises);
    }

    setInnerHTML(this.content.caption.firstElementChild, html);
    this.content.caption.classList.toggle('hide', !hasCaption);
    // this.content.container.classList.toggle('with-caption', !!caption);
  }

  private removeTimestamps() {
    this.videoTimestamps = [];
  }

  private async saveTimestamps(messageContent: HTMLElement, loadPromises: Promise<any>[]) {
    loadPromises && await Promise.all(loadPromises);
    const timestampElements = Array.from(messageContent.querySelectorAll('.timestamp[data-timestamp]'));

    this.videoTimestamps = timestampElements.map((element) => ({
      time: +(element as HTMLElement).dataset.timestamp,
      text: this.extractTimestampText(element)
    }));
  }

  private extractTimestampText(element: Element) {
    const result: string[] = [];
    let current = element.nextSibling;
    while(current) {
      if(current instanceof HTMLElement && current.classList.contains('timestamp')) break;

      const text = current.textContent;

      const shouldBreak = text.includes('\n');
      result.push(text.split('\n')[0].trim());

      if(shouldBreak) break;
      current = current.nextSibling;
    }

    return result.filter(Boolean).join(' ');
  }

  public setSearchContext(context: MediaSearchContext) {
    this.listLoader.setSearchContext(context);

    return this;
  }

  private setMessageActionVisibility(options: {
    cantForward: boolean,
    cantCopy: boolean,
    cantDownload: boolean,
    cantDelete: boolean,
    cantEdit: boolean,
    editLangKey?: LangPackKey
  }) {
    if(options.editLangKey) {
      this.btnMenuEditText.compareAndUpdate({key: options.editLangKey});
      this.buttons.edit.setAttribute('aria-label', I18n.format(options.editLangKey, true));
    }

    const actions: [(HTMLElement | ButtonMenuItemOptionsVerifiable)[], boolean][] = [
      [[this.buttons.edit, this.btnMenuEdit], options.cantEdit],
      [[this.buttons.forward, this.btnMenuForward], options.cantForward],
      [[this.buttons.copy, this.btnMenuCopy], options.cantCopy],
      [[this.buttons.download, this.btnMenuDownload], options.cantDownload],
      [[this.buttons.delete, this.btnMenuDelete], options.cantDelete]
    ];

    actions.forEach(([buttons, hide]) => {
      buttons.forEach((button) => {
        if(button instanceof HTMLElement) {
          button.classList.toggle('hide', hide);
        } else {
          button.verify = () => !hide;
        }
      });
    });

    this.wholeDiv.classList.toggle('no-forwards', options.cantDownload);
  }

  public openMedia({
    message,
    index,
    target,
    fromRight = 0,
    reverse = false,
    prevTargets = [],
    nextTargets = [],
    mediaTimestamp
  }: {
    message: MyMessage,
    index?: number,
    target?: HTMLElement,
    fromRight?: number,
    reverse?: boolean,
    prevTargets?: AppMediaViewerTargetType[],
    nextTargets?: AppMediaViewerTargetType[],
    mediaTimestamp?: number
    /* , needLoadMore = true */
  }) {
    if(this.setMoverPromise) return this.setMoverPromise;

    const mid = message.mid;
    const fromId = (message as Message.message).fwd_from && !message.fromId ? getFwdFromName((message as Message.message).fwd_from) : message.fromId;
    const media = getMediaFromMessage(message, true, index);

    const isSponsored = !!(message as Message.message).pFlags.sponsored || !message.fromId;
    const noAuthor = isSponsored;
    const isServiceMessage = message._ === 'messageService';
    const action = isServiceMessage ? (message as Message.messageService).action : undefined;
    const isChatPhotoEdit = !!action &&
      (action._ === 'messageActionChannelEditPhoto' || action._ === 'messageActionChatEditPhoto') &&
      message.peerId.isAnyChat();

    // "edit" reopens the media in the editor and replaces the message with the result,
    // so it needs the composer of the very chat this message lives in (see onEditClick)
    const canOpenEditor = !this.local &&
      !isSponsored &&
      canEditMessageMediaWithEditor(message as Message.message) &&
      appImManager.chat?.peerId === message.peerId &&
      !!appImManager.chat.input?.messageInput;

    // Start with a conservative state, but do not put worker-proxy permission checks
    // in front of the first animation frame. The controls reconcile as soon as all
    // the independent checks settle.
    this.deleteAsChatPhoto = false;
    this.setMessageActionVisibility({cantForward: true, cantCopy: true, cantDownload: true, cantDelete: true, cantEdit: true});
    const permissionsPromise = Promise.all([
      this.managers.appPeersManager.noForwards(message.peerId),
      isServiceMessage || noAuthor ? Promise.resolve(false) : this.managers.appMessagesManager.canForward(message),
      isChatPhotoEdit ?
        this.managers.appChatsManager.hasRights(message.peerId.toChatId(), 'change_info') :
        this.managers.appMessagesManager.canDeleteMessage(message),
      canOpenEditor ? this.managers.appMessagesManager.canEditMessage(message, 'text') : Promise.resolve(false)
    ]);

    this.removeTimestamps();
    this.setCaption(message);

    const promise = super._openMedia({
      media: media as MyPhoto | MyDocument,
      timestamp: message.date,
      fromId,
      fromRight,
      target,
      reverse,
      prevTargets,
      nextTargets,
      message,
      mediaTimestamp,
      noAuthor
      /* , needLoadMore */
    });
    this.target.mid = mid;
    this.target.peerId = message.peerId;
    this.target.message = message;
    this.target.index = index;

    void permissionsPromise.then(([noForwards, canForward, canDelete, canEdit]) => {
      if(this.target?.message !== message) return;
      const cantForward = !canForward;
      const cantDownload = (isServiceMessage ? noForwards : cantForward && !isSponsored) || !canSaveMessageMedia(message, noForwards);
      const cantCopy = cantDownload || !canCopyMediaToClipboard(media);
      const cantDelete = !canDelete;
      this.deleteAsChatPhoto = isChatPhotoEdit && !cantDelete;
      this.setMessageActionVisibility({
        cantForward,
        cantCopy,
        cantDownload,
        cantDelete,
        cantEdit: !canEdit,
        editLangKey: getEditMediaLangKey(message as Message.message)
      });
    }).catch((error) => {
      this.log.warn('failed to resolve media viewer actions', error);
    });

    // Animated avatar (a profile/chat photo with video_sizes — e.g. a group /
    // channel avatar-change service message): overlay the looping video on the
    // still image once the open/move animation settles, like the avatar viewer.
    this.videoAvatarCleanup?.();
    this.videoAvatarCleanup = undefined;
    if((media as MyPhoto)?._ === 'photo' && (media as MyPhoto).video_sizes?.length) {
      const photo = media as MyPhoto;
      Promise.resolve(promise).then(() => {
        if(this.target?.message !== message || !this.content.mover) return;
        this.videoAvatarCleanup = overlayAvatarVideoOnMover(this.content.mover, photo);
      });
    }

    return promise;
  }

  public close(e?: MouseEvent) {
    this.videoAvatarCleanup?.();
    this.videoAvatarCleanup = undefined;
    return super.close(e);
  }

  public static isMediaCompatibleForDocumentViewer(media: MyPhoto | MyDocument) {
    return (media._ === 'photo' || MEDIA_MIME_TYPES_SUPPORTED.has(media.mime_type) && media.size <= MAX_FILE_SAVE_SIZE);
  }
}
